import { FormField } from "../../types/forms";
import { Input } from "../../components/ui/input2";
import { Textarea } from "../../components/ui/Textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

interface QuestionSectionProps {
  userId: string;
  templateId: string;
  isEditable?: boolean;
  readOnly?: boolean;
  fields?: FormField[];
  value?: any;
  onChange?: (value: any) => void;
}

export default function QuestionSection({
  fields,
  value,
  onChange,
  readOnly = false,
}: QuestionSectionProps) {
  // Extract configuration from special fields
  const questionField = fields?.find(f => f.label === "Question Text");
  const isMetadataDriven = !!questionField;

  const handleValueChange = (fieldId: string | number, newVal: any) => {
    const currentValues = (typeof value === "object" && value !== null) ? value : {};
    onChange?.({ ...currentValues, [fieldId]: newVal });
  };

  const renderInput = (
    type: string,
    qLabel: string,
    qId: string | number,
    currentVal: any,
    opts: string[]
  ) => {
    // Normalize backend display labels to frontend internal values
    const typeMap: Record<string, string> = {
      "Multiple Choice": "radio",
      Checkboxes: "checkbox_group",
      Dropdown: "select",
      "Short Answer": "text",
      Paragraph: "textarea",
      Date: "date",
    };
    const answerType = typeMap[type] || type;

    switch (answerType) {
      case "textarea":
        return (
          <Textarea
            placeholder="Type your answer here..."
            className="min-h-[100px]"
            value={currentVal || ""}
            onChange={(e) => handleValueChange(qId, e.target.value)}
            disabled={readOnly}
          />
        );
      case "radio":
        return (
          <div className="space-y-2">
            {opts.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`radio-${qLabel}-${qId}`}
                  checked={currentVal === opt}
                  onChange={() => handleValueChange(qId, opt)}
                  className="w-4 h-4 accent-[#885ABB] cursor-pointer disabled:cursor-not-allowed"
                  disabled={readOnly}
                />
                <span className="text-sm text-gray-600">{opt}</span>
              </div>
            ))}
          </div>
        );
      case "checkbox_group":
        const vals = Array.isArray(currentVal) ? currentVal : [];
        const toggle = (opt: string) => {
          if (vals.includes(opt)) {
            handleValueChange(qId, vals.filter((v: string) => v !== opt));
          } else {
            handleValueChange(qId, [...vals, opt]);
          }
        };
        return (
          <div className="space-y-2">
            {opts.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={vals.includes(opt)}
                  onChange={() => toggle(opt)}
                  className="w-4 h-4 accent-[#885ABB] cursor-pointer disabled:cursor-not-allowed"
                  disabled={readOnly}
                />
                <span className="text-sm text-gray-600">{opt}</span>
              </div>
            ))}
          </div>
        );
      case "select":
        return (
          <Select
            value={currentVal}
            onValueChange={(val) => handleValueChange(qId, val)}
            disabled={readOnly}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select an option..." />
            </SelectTrigger>
            <SelectContent>
              {opts.map((opt, i) => (
                <SelectItem key={i} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "date":
        return (
          <Input
            type="date"
            className="w-full"
            value={currentVal || ""}
            onChange={(e) => handleValueChange(qId, e.target.value)}
            disabled={readOnly}
          />
        );
      case "text":
      default:
        return (
          <Input
            placeholder="Type your answer here..."
            value={currentVal || ""}
            onChange={(e) => handleValueChange(qId, e.target.value)}
            disabled={readOnly}
          />
        );
    }
  };

  if (isMetadataDriven) {
    const questionLabel =
      questionField.options?.content ||
      questionField.content ||
      "Untitled Question";
    const questionFieldId = questionField.id;
    const rawAnswerType =
      fields?.find((f) => f.label === "Answer Type")?.options?.content ||
      fields?.find((f) => f.label === "Answer Type")?.content ||
      "text";
    const requiredField = fields?.find((f) => f.label === "Required Field");
    const isRequired =
      String(requiredField?.options?.content) === "true" ||
      String(requiredField?.content) === "true";
    const helpText =
      fields?.find((f) => f.label === "Help Text")?.options?.content ||
      fields?.find((f) => f.label === "Help Text")?.content;
    const optionsField = fields?.find((f) => Array.isArray(f.options));
    const options = (optionsField?.options as string[]) || [
      "Option 1",
      "Option 2",
      "Option 3",
    ];

    const currentVal =
      typeof value === "object" && value !== null ? value[questionFieldId] : value;

    return (
      <div className="space-y-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-[15px] font-bold text-gray-800 tracking-tight">
            {questionLabel}
            {isRequired && <span className="text-red-500 ml-1.5 font-bold">*</span>}
          </label>
          {helpText && <p className="text-[13px] text-gray-500 leading-relaxed font-medium">{helpText}</p>}
        </div>
        <div className="mt-3">
          {renderInput(
            rawAnswerType,
            questionLabel,
            questionFieldId,
            currentVal,
            options
          )}
        </div>
      </div>
    );
  }

  // Baked/Field-driven mode
  return (
    <div className="space-y-8">
      {fields?.map((field) => (
        <div key={field.id} className="space-y-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[15px] font-bold text-gray-800 tracking-tight">
              {field.label}
              {field.required && <span className="text-red-500 ml-1.5 font-bold">*</span>}
            </label>
            {field.help_text && (
              <p className="text-[13px] text-gray-500 leading-relaxed font-medium">{field.help_text}</p>
            )}
          </div>
          <div className="mt-3">
            {renderInput(
              field.type,
              field.label,
              field.id,
              typeof value === "object" && value !== null ? value[field.id] : null,
              Array.isArray(field.options) ? field.options : []
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
